import { Component, OnInit } from '@angular/core';
import { ParkingReportService } from './parking-report.service';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from 'src/app/_services';
import { ThisPlatform } from 'src/app/_services/platform.service';
import { DateTimeAdapter } from '@danielmoncada/angular-datetime-picker';
import { Subscription } from 'rxjs';
import { ParkingReport, ParkingReportType } from './parking-report.model';
import { RowsLoaderService } from 'src/app/_services/rows-loader.service';
import { FormGroup } from '@angular/forms';
import { BuildingService } from '../../building/building.service';
import { CompanyService } from '../../company/company.service';
import { PaginationColumn } from '../../_component/pagination-client-side/pagination-column.class';
import { PaginationClientSideOptions } from '../../_component/pagination-client-side/pagination-client-side-options';
import { GetBookingByUserModel } from 'src/app/_models/booking-request.model';
import { SpaceType } from 'src/app/_types/space-type';
import { BookingService } from 'src/app/_services/booking.service';
import { BookingModel } from 'src/app/_models/booking.model';
import { ParkingReportViewComponent } from './parking-report-view/parking-report-view.component';
import { ParkingReportModalFilterComponent } from './parking-report-modal-filter/parking-report-modal-filter.component';
import { ModalController } from '@ionic/angular';
import { preventSwipeToClose } from 'src/app/_helpers/preventSwipeToClose';
import { Utils } from 'src/app/_helpers/utils';
import { DictionaryService } from 'src/app/dictionary';

@Component({
  selector: 'app-parking-report',
  templateUrl: './parking-report.component.html',
  styleUrls: [
    './parking-report.component.scss',
    './../../_component/_space/space-filter/space-filter.component.scss',
  ],
})

export class ParkingReportComponent implements OnInit {

  parkingReports: ParkingReport[];

  rowsLoader: RowsLoaderService;
  showLoader: boolean = true;
  form: FormGroup;

  subscription: Subscription = new Subscription();

  /** Rows component configuration */
  componentRef: any = ParkingReportViewComponent;
  
  columns: PaginationColumn[] = [{
      name: 'building',
      displayName: 'SPACE.BUILDING',
      size: 2,
      sortable: true,
      colValue: (row) => {
        return row.bookingSpaces[0].space.floor.building.name;
      },
    }, {
      name: 'level',
      displayName: 'SPACE.LEVEL',
      size: 2,
      sortable: true,
      colValue: (row) => {
        return row.bookingSpaces[0].space.floor.name;
      },
    }, {
      name: 'place',
      displayName: 'SPACE.PLACE',
      size: 2,
      sortable: true,
      colValue: (row) => {
        return row.bookingSpaces[0].space.name;
      },
    }, {
      name: 'startDate',
      displayName: 'SPACE.RESERVATION_START',
      size: 3,
      sortable: true,
      colValue: (row) => {
        return row.startTime;
      },
    }, {
      name: 'endDate',
      displayName: 'SPACE.RESERVATION_END',
      size: 3,
      sortable: true,
      colValue: (row) => {
        return row.endTime;
      },
    }, {
      name: 'number',
      displayName: 'SPACE.PARKING.PLATE_NUMBER',
      size: 2,
      sortable: true,
      colValue: (row) => {
        return row.bookingSpaces[0].number
          ? row.bookingSpaces[0].number
          : row.userOwner.carPlate;
      },
    }, {
      name: 'name',
      displayName: 'SPACE.ASSIGNED_PERSON',
      size: 2,
      sortable: true,
      colValue: (row) => {
        return row.userOwner.firstName + row.userOwner.lastName;
      },
    }, {
      name: 'status',
      displayName: 'REPORT.STATUS',
      size: 2,
      sortable: true,
      colValue: (row) => {
        return row.status;
      },
    }
  ];

  options: PaginationClientSideOptions = {
    ...new PaginationClientSideOptions(),
    mobileViewBreakPoint: 992,
    headerStickyPosition: 395,
    mobileFilterButton: true,
    gridColumnsCount: 18,
    styleClasses: ["content-table"],
  };

  constructor(
    public parkingReportServ: ParkingReportService,
    public translateService: TranslateService,
    public p: ThisPlatform,
    public buildingServ: BuildingService,
    public companyServ: CompanyService,
    private userServ: UserService,
    private dateTimeAdapter: DateTimeAdapter<any>,
    private bookingService: BookingService,
    private modalCtrl: ModalController,
    private dictionaryServ: DictionaryService,
  ) { }

  async ngOnInit() {
    this.chartVariablesLoad();
    this.getParkingReports();
    this.getParkingBookings();

    this.dateTimeAdapter.setLocale(this.userServ.getDefaultLanguage());

    await this.buildingServ.loadBuildingsSync();
    await this.companyServ.loadCompaniesSync();

    this.subscription.add(
      this.parkingReportServ.filterDetailsSubject.subscribe({
        next: (value) => {
          value ? this.getData() : this.clearFilters();
        }
      })
    );

    this.onRefreshChart();
  }

  async onToggleFilter() { 
    const modal = await this.modalCtrl.create({
      component: ParkingReportModalFilterComponent,
      canDismiss: preventSwipeToClose,
      cssClass: "add-edit-modal mobile-filter__modal-container",
    });

    modal.present();
  }

  /** set building param filter */
  onFilterBuilding($event) { 
    this.parkingReportServ.request.building = $event;
    this.getData();
  }

  /** set company param filter */
  onFilterCompany($company) {
    this.parkingReportServ.request.company = $company;
    this.getData();
  }

  /** set date param filter */
  onFilterDate(date, whichDate: string = "start" || "end") {
    if (whichDate == "start") {
      this.parkingReportServ.request.dateFrom = date;
    } else if (whichDate == "end") {
      this.parkingReportServ.request.dateTo = date;
    }

    this.getData();
  }

  /** set up charts variables */
  chartVariablesLoad() {
    this.parkingReportServ.defaultLocale = this.userServ.getDefaultLanguage();
    this.parkingReportServ.chartHeader = this.translateService.instant(
      "ADMIN.GUEST_REPORT.HEADER"
    );
    this.parkingReportServ.entires = this.translateService.instant(
      "ADMIN.GUEST_REPORT.ENTRY_QUANTITY"
    );
    this.parkingReportServ.exits = this.translateService.instant(
      "ADMIN.GUEST_REPORT.EXIT_QUANTITY"
    );
    this.parkingReportServ.noDataText = this.translateService.instant(
      "NO_DATA_TO_SHOW"
    );

    if (this.p.renderMobile) {
      this.parkingReportServ.chartHeaderSize = "14px";
    } else {
      this.parkingReportServ.chartHeaderSize = "28px";
    }
  }

  /** set rows loader */
  rowsLoaderServiceReady(loader: RowsLoaderService) {
    this.showLoader = false;
    this.rowsLoader = loader;
    this.rowsLoaderInit();
  }

  /** init rows loader */
  rowsLoaderInit() {
    if (this.rowsLoader) {
      this.showLoader = false;
      this.parkingReportServ.filterDetailsShowLoader = false;
      this.rowsLoader.init(this.parkingReportServ.parkingBookings);
    }
  }

  /** refresh rows loader */
  rowsLoaderRefresh() {
    this.showLoader = false;
    this.rowsLoader.init(this.parkingReportServ.parkingBookings);
  }

  /** get parking reports */
  getParkingReports() {
    this.parkingReportServ.requestParameters = {
      type: ParkingReportType.PARKING.toString(),
      dateFrom: this.parkingReportServ.request.dateFrom
        ? this.parkingReportServ.request.dateFrom
        : this.monthBeforeCurrentDate().toISOString(),
      dateTo: this.parkingReportServ.request.dateTo
        ? this.parkingReportServ.request.dateTo
        : this.currentDate().toISOString(),
      idBuilding: this.parkingReportServ.request.building
        ? this.parkingReportServ.request.building.id
        : null,
      idCompany: this.parkingReportServ.request.company
        ? this.parkingReportServ.request.company.id
        : null,
    };

    this.parkingReportServ.request = {
      ...this.parkingReportServ.requestParameters,
      building: this.parkingReportServ.request.building,
      company: this.parkingReportServ.request.company,
    }

    this.subscription.add(
      this.parkingReportServ
        .getParkingReports(this.parkingReportServ.requestParameters)
        .subscribe({
          next: (res) => {
            this.parkingReportServ.parkingReports = this.parkingReports = res;
          },
          error: (err) => {
            console.log(err);
          },
          complete: () => {
            this.showLoader = false;
            this.onRefreshChart();
          }
        }
      )
    );
  }

  /** refresh chart */
  onRefreshChart() {
    this.parkingReportServ.loadChart(
      this.parkingReportServ.parkingReports
    );
  }

  /** get parking bookings */
  getParkingBookings() {
    let requestParams: GetBookingByUserModel = {
      spaceType: SpaceType.Parking,
      order: 'DESC',
      startTimeGreatherThan: this.parkingReportServ.request.dateFrom
        ? this.parkingReportServ.request.dateFrom
        : this.monthBeforeCurrentDate().toDateString(),
      endTimeLessThan: this.parkingReportServ.request.dateTo
        ? this.parkingReportServ.request.dateTo
        : this.currentDate().toDateString(),
      buildings:  this.parkingReportServ.request.building
        ? this.parkingReportServ.request.building.id.toString()
        : null,
      idCompany: this.parkingReportServ.request.company 
        ? this.parkingReportServ.request.company.id
        : null,
      includeDeleted: true
    };

    this.showLoader = true;
    this.subscription.add(
      this.bookingService
      .getBookingByUser(requestParams)
      .subscribe({
        next: (bookings: BookingModel[]) => {
          this.showLoader = false;
          this.parkingReportServ.parkingBookings = bookings;
          this.rowsLoaderRefresh();
        }
      })
    );
  }

  async exportToCSV() {
    // create header row for csv
    let headers = this.columns.map(column => column.name);

    const data = this.parkingReportServ.parkingBookings
      .map(item => ({
        building: item.bookingSpaces[0].space.floor.building.name,
        level: item.bookingSpaces[0].space.floor.name,
        place: item.bookingSpaces[0].space.name,
        startDate: item.startTime,
        endDate: item.endTime,
        number: item.bookingSpaces[0].number ? item.bookingSpaces[0].number : item.userOwner.carPlate,
        name: item.userOwner.firstName + item.userOwner.lastName,
        status: item.status
      })
    )

    // download file
    Utils.downloadCSVFile(data, 'parking_report', headers)
  }

  private currentDate(): Date {
    return new Date(Date.now())
  }

  private monthBeforeCurrentDate(): Date {
    return new Date(new Date().setDate(new Date().getDate() - 30))
  }

  private getData() {
    this.getParkingReports();
    this.getParkingBookings();
  }

  private clearFilters() { 
    this.parkingReportServ.request = {
      type: ParkingReportType.PARKING.toString(),
      dateFrom: null,
      dateTo: null,
      building: null,
      company: null,
    }

    this.getData();
  }

  ngOnDestroy() {
    this.parkingReportServ.request = {
      type: ParkingReportType.PARKING.toString(),
      dateFrom: null,
      dateTo: null,
      building: null,
      company: null,
    }

    this.subscription?.unsubscribe();
  }

}
